import { Icon } from "@iconify/react";
import { useRef, useState } from "react";
import { useFormikContext } from "formik";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useGetAssociatedUsers } from "../../queries/usersRoles";
import { SwitchButton } from "../../components/shared/SwitchButton";
import { RadioGroup, RadioGroupItem } from "../../components/shared/RadioGroup";
import { Select } from "../../components/shared/Select";
import {
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import {
  AddPlanReq,
  ApprovalOrder,
  GrantApprovalConfig,
  GrantApproverEntry,
} from "../../types/EsopPlan";
import { AssociatedUsers } from "../../utils/interfaces/Companies";

const ELIGIBLE_ROLES = ["APPROVER", "MANAGER"];

export const RESPECTIVE_MANAGER_SENTINEL = "RESPECTIVE_MANAGER";

const ROLE_BADGE_STYLES: Record<string, string> = {
  MANAGER: "bg-purple-50 text-purple-600",
  APPROVER: "bg-blue-50 text-blue-600",
};

function isEligibleApprover(user: AssociatedUsers): boolean {
  const roleName = user.role?.name?.toUpperCase() ?? "";
  return ELIGIBLE_ROLES.includes(roleName);
}

const defaultConfig: GrantApprovalConfig = {
  requireSignOff: false,
  approvalOrder: "SEQUENTIAL",
  approvers: [],
};

type ApproverRowProps = {
  id: string;
  approver: GrantApproverEntry;
  index: number;
  isView: boolean;
  isExpanded: boolean;
  submitAttempted: boolean;
  roleOptions: { value: string; text: string }[];
  nameOptions: { value: string; text: string }[];
  onToggleExpand: () => void;
  onRemove: () => void;
  onRoleSelect: (role: string) => void;
  onNameSelect: (userId: string) => void;
  onExpiryDaysChange: (days: number | undefined) => void;
  onExpiryActionChange: (action: string | undefined) => void;
};

function ApproverRow({
  id,
  approver,
  index,
  isView,
  isExpanded,
  submitAttempted,
  roleOptions,
  nameOptions,
  onToggleExpand,
  onRemove,
  onRoleSelect,
  onNameSelect,
  onExpiryDaysChange,
  onExpiryActionChange,
}: ApproverRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isView });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  const isRespectiveManager =
    approver.role === "MANAGER" &&
    approver.userId === RESPECTIVE_MANAGER_SENTINEL;
  const roleBadgeClass =
    ROLE_BADGE_STYLES[approver.role] ?? "bg-gray-100 text-gray-500";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl border bg-white overflow-hidden transition-shadow ${
        isDragging ? "border-orange-200 shadow-lg" : "border-borderColor"
      }`}
    >
      {/* Header: drag handle + order + approver summary + expand/remove */}
      <div
        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/70 cursor-pointer"
        onClick={() => !isView && onToggleExpand()}
      >
        {!isView && (
          <button
            type="button"
            className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            title="Drag to reorder"
          >
            <Icon icon="mdi:drag-vertical" width={18} />
          </button>
        )}
        <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold flex items-center justify-center shrink-0">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <HStack className="items-center gap-2">
            <p className="text-sm font-medium text-primaryText truncate">
              {approver.name || "Select approver"}
            </p>
            {approver.role && (
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm shrink-0 ${roleBadgeClass}`}
              >
                {approver.role}
              </span>
            )}
          </HStack>
          {isRespectiveManager ? (
            <p className="text-xs text-gray-400 truncate">
              Resolves to every manager assigned to the employee
            </p>
          ) : (
            approver.email && (
              <p className="text-xs text-gray-400 truncate">{approver.email}</p>
            )
          )}
        </div>
        <HStack className="gap-2 shrink-0">
          {!isView && (
            <>
              <button
                type="button"
                className="text-gray-400 hover:text-primaryText transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand();
                }}
              >
                <Icon
                  icon={
                    isExpanded ? "ri:arrow-up-s-line" : "ri:arrow-down-s-line"
                  }
                  width={20}
                />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <Icon icon="material-symbols:delete-outline" width={20} />
              </button>
            </>
          )}
        </HStack>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-borderColor px-4 py-4 bg-gray-50">
          <VStack className="gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">
                  Select Role
                </label>
                <Select
                  disabled={isView}
                  options={roleOptions}
                  valueGetter={(o) => o.value}
                  textGetter={(o) => o.text}
                  value={approver.role}
                  onChange={(e) => onRoleSelect(e.target.value)}
                />
                {submitAttempted && !approver.role && (
                  <Error text="Role is required" />
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">
                  Select Name
                </label>
                <Select
                  disabled={isView || !approver.role}
                  options={nameOptions}
                  valueGetter={(o) => o.value}
                  textGetter={(o) => o.text}
                  value={approver.userId}
                  onChange={(e) => onNameSelect(e.target.value)}
                />
                {submitAttempted && !approver.userId && (
                  <Error text="Name is required" />
                )}
              </div>
            </div>

            {approver.role === "MANAGER" &&
              approver.userId &&
              isRespectiveManager && (
                <div className="flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2">
                  <Icon
                    icon="mdi:information-outline"
                    width={16}
                    className="text-blue-500 mt-0.5 shrink-0"
                  />
                  <p className="text-xs text-blue-700">
                    An employee can have more than one manager — a separate
                    approval is created for every manager assigned to the
                    employee at grant time.
                  </p>
                </div>
              )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">
                  Expiry Days (optional)
                </label>
                <input
                  type="number"
                  min={1}
                  disabled={isView}
                  value={approver.expiryDays ?? ""}
                  onChange={(e) =>
                    onExpiryDaysChange(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="e.g., 5"
                  className="form-input w-full text-sm disabled:bg-white disabled:border-gray-200 disabled:cursor-default"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Days until approval expires
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">
                  On Expiry Action (optional)
                </label>
                <select
                  disabled={isView}
                  value={approver.onExpiryAction ?? ""}
                  onChange={(e) =>
                    onExpiryActionChange(e.target.value || undefined)
                  }
                  className="form-input w-full text-sm disabled:bg-white disabled:border-gray-200 disabled:cursor-default"
                >
                  <option value="">— Select action —</option>
                  <option value="AUTO_APPROVE">Auto Approve</option>
                  <option value="AUTO_REJECT">Auto Reject</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Action if no response by deadline
                </p>
              </div>
            </div>
          </VStack>
        </div>
      )}
    </div>
  );
}

type Props = {
  onBack: () => void;
  onNext: () => void;
  mode?: string | null;
};

export function GrantApprovalStep({ onBack, onNext, mode }: Props) {
  const formik = useFormikContext<AddPlanReq>();
  const { data: associatedUsers } = useGetAssociatedUsers();
  const isView = mode === "view";

  const config: GrantApprovalConfig =
    formik.values.grantApprovalConfig ?? defaultConfig;

  const rowIdCounter = useRef(0);
  function generateRowId() {
    rowIdCounter.current += 1;
    return `approver-row-${rowIdCounter.current}`;
  }
  const [rowIds, setRowIds] = useState<string[]>(() =>
    config.approvers.map(() => generateRowId())
  );
  const [expandedApprovers, setExpandedApprovers] = useState<Set<string>>(
    () => new Set(rowIds.length ? [rowIds[0]] : [])
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const eligibleUsers = (associatedUsers ?? []).filter(isEligibleApprover);

  const roleOptions = ELIGIBLE_ROLES.map((role) => ({
    value: role,
    text: role,
  }));

  function getNameOptionsForRole(role: string) {
    const nameOptions = eligibleUsers
      .filter((u) => u.role?.name?.toUpperCase() === role)
      .map((u) => ({
        value: u.userId?.toString() ?? u.email,
        text: u.name,
      }));
    if (role === "MANAGER") {
      return [
        { value: RESPECTIVE_MANAGER_SENTINEL, text: "Respective Manager(s)" },
        ...nameOptions,
      ];
    }
    return nameOptions;
  }

  function setConfig(updates: Partial<GrantApprovalConfig>) {
    formik.setFieldValue("grantApprovalConfig", { ...config, ...updates });
  }

  function handleToggle() {
    if (isView) return;
    setConfig({ requireSignOff: !config.requireSignOff });
  }

  function handleApprovalOrderChange(value: string) {
    if (isView) return;
    setConfig({ approvalOrder: value as ApprovalOrder });
  }

  function handleAddApprover() {
    const newEntry: GrantApproverEntry = {
      userId: "",
      name: "",
      email: "",
      role: "",
      order: config.approvers.length + 1,
      expiryDays: undefined,
      onExpiryAction: "AUTO_APPROVE",
    };
    const newId = generateRowId();
    setRowIds((prev) => [...prev, newId]);
    setExpandedApprovers((prev) => new Set(prev).add(newId));
    setConfig({ approvers: [...config.approvers, newEntry] });
  }

  function handleRemoveApprover(idx: number) {
    const removedId = rowIds[idx];
    const updatedApprovers = config.approvers
      .filter((_, i) => i !== idx)
      .map((a, i) => ({ ...a, order: i + 1 }));
    setRowIds((prev) => prev.filter((_, i) => i !== idx));
    setExpandedApprovers((prev) => {
      const next = new Set(prev);
      next.delete(removedId);
      return next;
    });
    setConfig({ approvers: updatedApprovers });
  }

  function handleApproverRoleSelect(idx: number, role: string) {
    const updated = config.approvers.map((a, i) =>
      i === idx ? { ...a, role, userId: "", name: "", email: "" } : a
    );
    setConfig({ approvers: updated });
  }

  function handleApproverNameSelect(idx: number, userId: string) {
    if (userId === RESPECTIVE_MANAGER_SENTINEL) {
      const updated = config.approvers.map((a, i) =>
        i === idx
          ? {
              ...a,
              userId: RESPECTIVE_MANAGER_SENTINEL,
              name: "Respective Manager(s)",
              email: "",
            }
          : a
      );
      setConfig({ approvers: updated });
      return;
    }
    const user = eligibleUsers.find(
      (u) => (u.userId?.toString() ?? u.email) === userId
    );
    if (!user) return;
    const updated = config.approvers.map((a, i) =>
      i === idx
        ? {
            ...a,
            userId: user.userId?.toString() ?? user.email,
            name: user.name,
            email: user.email,
          }
        : a
    );
    setConfig({ approvers: updated });
  }

  function handleExpiryDaysChange(idx: number, days: number | undefined) {
    const updated = config.approvers.map((a, i) =>
      i === idx ? { ...a, expiryDays: days } : a
    );
    setConfig({ approvers: updated });
  }

  function handleOnExpiryActionChange(idx: number, action: string | undefined) {
    const updated = config.approvers.map((a, i) =>
      i === idx ? { ...a, onExpiryAction: action as any } : a
    );
    setConfig({ approvers: updated });
  }

  function toggleExpandedApprover(id: string) {
    setExpandedApprovers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rowIds.indexOf(active.id as string);
    const newIndex = rowIds.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedApprovers = arrayMove(
      config.approvers,
      oldIndex,
      newIndex
    ).map((a, i) => ({ ...a, order: i + 1 }));
    setRowIds(arrayMove(rowIds, oldIndex, newIndex));
    setConfig({ approvers: reorderedApprovers });
  }

  function handleNext() {
    setSubmitAttempted(true);
    if (config.requireSignOff) {
      const hasIncompleteApprover = config.approvers.some(
        (a) => !a.role || !a.userId
      );
      if (config.approvers.length === 0 || hasIncompleteApprover) {
        setExpandedApprovers(new Set(rowIds));
        return;
      }
    }
    onNext();
  }

  return (
    <>
      <VStack className="w-full gap-6 py-6 pr-7">
        <div className="flex justify-start text-lg font-medium">
          <span>Configure Grant Approvals</span>
        </div>

        {/* Require Sign-off toggle */}
        <div className="border border-borderColor rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-primaryText">
                Require Sign-Off
              </div>
              <div className="text-xs text-secondaryText mt-0.5">
                Require specific team members to approve grants before they are
                finalized.
              </div>
            </div>
            <div className="flex items-center justify-end shrink-0">
              <SwitchButton
                value={config.requireSignOff}
                label=""
                disabled={isView}
                onClick={handleToggle}
              />
            </div>
          </div>
        </div>

        {config.requireSignOff && (
          <>
            {/* Approval Logic */}
            <VStack className="gap-3">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                Approval Logic
              </p>
              <RadioGroup
                value={config.approvalOrder}
                onValueChange={handleApprovalOrderChange}
                disabled={isView}
                className="grid grid-cols-2 gap-4"
              >
                {(
                  [
                    {
                      value: "SEQUENTIAL",
                      label: "Sequential Approval",
                      description:
                        "Approvers must sign off in the specific order listed.",
                    },
                    {
                      value: "NO_ORDER",
                      label: "No Order",
                      description: "Any approver can sign off at any time.",
                    },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex cursor-pointer gap-3 rounded-lg border-2 p-4 transition-colors ${
                      config.approvalOrder === opt.value
                        ? "border-orange-501 bg-white"
                        : "border-gray-200 bg-white"
                    } ${isView ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    <div className="pt-0.5">
                      <RadioGroupItem value={opt.value} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {opt.label}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {opt.description}
                      </p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </VStack>

            {/* Approvers List */}
            <VStack className="gap-3">
              <div className="flex items-baseline justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Approvers List
                </p>
                {!isView && config.approvers.length > 1 && (
                  <span className="text-[11px] text-gray-400">
                    {config.approvalOrder === "SEQUENTIAL"
                      ? "Drag to change sign-off order"
                      : "Order doesn't affect sign-off — drag only to organize"}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400">
                If Expiry Days and On Expiry Action are left empty for an
                approver, no automatic action will be taken when that approver
                does not respond.
              </p>
              {submitAttempted && config.approvers.length === 0 && (
                <Error text="At least one approver is required when Require Sign-Off is on." />
              )}

              {config.approvers.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">
                  No approvers added yet.
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={rowIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <VStack className="gap-2">
                      {config.approvers.map((approver, idx) => (
                        <ApproverRow
                          key={rowIds[idx]}
                          id={rowIds[idx]}
                          approver={approver}
                          index={idx}
                          isView={isView}
                          isExpanded={expandedApprovers.has(rowIds[idx])}
                          submitAttempted={submitAttempted}
                          roleOptions={roleOptions}
                          nameOptions={getNameOptionsForRole(approver.role)}
                          onToggleExpand={() =>
                            toggleExpandedApprover(rowIds[idx])
                          }
                          onRemove={() => handleRemoveApprover(idx)}
                          onRoleSelect={(role) =>
                            handleApproverRoleSelect(idx, role)
                          }
                          onNameSelect={(userId) =>
                            handleApproverNameSelect(idx, userId)
                          }
                          onExpiryDaysChange={(days) =>
                            handleExpiryDaysChange(idx, days)
                          }
                          onExpiryActionChange={(action) =>
                            handleOnExpiryActionChange(idx, action)
                          }
                        />
                      ))}
                    </VStack>
                  </SortableContext>
                </DndContext>
              )}

              {!isView && (
                <ButtonPrimary1
                  type="button"
                  onClick={handleAddApprover}
                  className="self-start"
                >
                  <Icon icon="ic:baseline-plus" width={16} />
                  Add another approver
                </ButtonPrimary1>
              )}

              {config.approvers.length > 0 &&
                config.approvalOrder === "NO_ORDER" && (
                  <div className="flex items-center gap-3 pt-1">
                    <label className="text-sm text-gray-600 shrink-0">
                      Minimum approvers required to proceed
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={config.approvers.length}
                      disabled={isView}
                      value={config.minApprovers ?? 1}
                      onChange={(e) =>
                        setConfig({ minApprovers: Number(e.target.value) })
                      }
                      className="form-input w-20 text-sm disabled:bg-transparent disabled:border-transparent disabled:cursor-default"
                    />
                    <span className="text-xs text-gray-400">
                      out of {config.approvers.length}
                    </span>
                  </div>
                )}
            </VStack>
          </>
        )}
      </VStack>

      <HStack className="justify-end gap-8 py-8 pr-8">
        <ButtonPrimary1 className="px-5" onClick={onBack}>
          Go Back
        </ButtonPrimary1>
        <div className="flex items-center">
          <ButtonPrimary className="px-5" onClick={handleNext}>
            Go Next: Options Expiry
          </ButtonPrimary>
        </div>
      </HStack>
    </>
  );
}
